/**
 * <ResetScreen /> — 20-30s breathing arrival.
 *
 * No timer pressure. No buttons. A soft breathing orb. Three calm lines
 * cycle through: "Arrive… / There's nothing to learn… / Just breathe…"
 * Auto-advances when the suggested duration elapses.
 *
 * Per Absorption Flow v3 spec: arrival is passive, no interaction.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { JOURNEY_PALETTE as P, JOURNEY_FONT_HEAD, JOURNEY_FONT_LABEL } from "./journeyTheme";

const ARRIVAL_LINES = [
  "Arrive…",
  "There's nothing to learn…",
  "Just breathe…",
];

export default function ResetScreen({
  guidance,                   // (legacy — ignored when not provided)
  durationSeconds = 28,
  onContinue,
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-advance at the end of the arrival window.
  useEffect(() => {
    if (elapsed >= durationSeconds) {
      const t = setTimeout(() => onContinue?.(), 600);
      return () => clearTimeout(t);
    }
  }, [elapsed, durationSeconds, onContinue]);

  // Decide which line to show — cycle through the three.
  // Each line gets ~ durationSeconds / 3 of airtime.
  const slot = Math.min(
    ARRIVAL_LINES.length - 1,
    Math.floor((elapsed / Math.max(1, durationSeconds)) * ARRIVAL_LINES.length)
  );
  // If a custom guidance was supplied (legacy), prefer that as a single line.
  const line = guidance || ARRIVAL_LINES[slot];

  return (
    <div
      className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden"
      style={{ background: P.bgMorningWash }}
      data-testid="journey-reset"
    >
      {/* ── Faint horizon depth on navy ─────────────────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 56%, rgba(77,163,255,0.18) 0%, rgba(77,163,255,0) 70%)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0) 56%, rgba(46,123,214,0.10) 70%, rgba(46,123,214,0.06) 100%)",
        }}
        aria-hidden
      />
      {/* Subtle horizon line */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: "10%",
          right: "10%",
          top: "62%",
          height: 1,
          background:
            "linear-gradient(90deg, transparent 0%, rgba(196,222,255,0.18) 35%, rgba(196,222,255,0.18) 65%, transparent 100%)",
        }}
        aria-hidden
      />

      {/* Breathing orb */}
      <div className="relative mb-12 flex items-center justify-center">
        <motion.div
          className="rounded-full"
          style={{
            width: 156,
            height: 156,
            background:
              "radial-gradient(circle at 50% 40%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 70%)",
          }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 7.2, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden
        />
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 36,
            height: 36,
          }}
          animate={{ scale: [1, 1.18, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 7.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-full h-full rounded-full"
            style={{
              background:
                "radial-gradient(circle at 50% 42%, rgba(255,255,255,0.95) 0%, rgba(196,222,255,0.55) 50%, rgba(196,222,255,0.05) 100%)",
              boxShadow:
                "0 0 80px rgba(77,163,255,0.45), 0 0 24px rgba(255,255,255,0.5)",
            }}
          />
        </motion.div>
      </div>

      <p
        className="text-[10px] uppercase tracking-[0.42em] mb-6 relative"
        style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
      >
        Arrival
      </p>

      <AnimatePresence mode="wait">
        <motion.p
          key={line}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 0.95, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          className="text-[22px] sm:text-[24px] text-center italic leading-[1.45] max-w-md relative"
          style={{
            color: P.textPrimary,
            fontFamily: JOURNEY_FONT_HEAD,
            fontWeight: 300,
            letterSpacing: "-0.005em",
          }}
          data-testid="arrival-line"
        >
          {line}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
